// File: webelieve-frontend/src/features/notes/services/notesApi.ts
// API client functions for notes management

import apiClient from '../../../api/axios';
import type {
  Note,
  NoteVersion,
  CreateNoteDto,
  UpdateNoteDto,
  NoteFilters,
  NoteListApiResponse,
  SingleNoteApiResponse,
  NoteVersionListApiResponse,
  NoteSearchApiResponse,
} from '../types/note.types';

const BASE_PATH = '/notes';

/**
 * Fetch notes with optional filters and pagination
 */
export async function fetchNotes(filters?: NoteFilters): Promise<NoteListApiResponse['data']> {
  const params: Record<string, string> = {};

  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params[key] = String(value);
      }
    });
  }

  const response = await apiClient.get<NoteListApiResponse>(BASE_PATH, { params });
  return response.data.data;
}

/**
 * Fetch a single note by ID
 */
export async function fetchNote(noteId: string): Promise<Note> {
  const response = await apiClient.get<SingleNoteApiResponse>(`${BASE_PATH}/${noteId}`);
  return response.data.data;
}

/**
 * Create a new note
 */
export async function createNote(dto: CreateNoteDto): Promise<Note> {
  const response = await apiClient.post<SingleNoteApiResponse>(BASE_PATH, dto);
  return response.data.data;
}

/**
 * Update an existing note
 */
export async function updateNote(noteId: string, dto: UpdateNoteDto): Promise<Note> {
  const response = await apiClient.put<SingleNoteApiResponse>(`${BASE_PATH}/${noteId}`, dto);
  return response.data.data;
}

/**
 * Delete a note (soft-delete)
 */
export async function deleteNote(noteId: string): Promise<void> {
  await apiClient.delete(`${BASE_PATH}/${noteId}`);
}

/**
 * Pin/unpin a note
 */
export async function pinNote(noteId: string, isPinned: boolean): Promise<Note> {
  const response = await apiClient.patch<SingleNoteApiResponse>(`${BASE_PATH}/${noteId}/pin`, {
    is_pinned: isPinned,
  });
  return response.data.data;
}

/**
 * Add tags to a note
 */
export async function addTags(noteId: string, tags: string[]): Promise<Note> {
  const response = await apiClient.post<SingleNoteApiResponse>(`${BASE_PATH}/${noteId}/tags`, { tags });
  return response.data.data;
}

/**
 * Remove tags from a note
 */
export async function removeTags(noteId: string, tags: string[]): Promise<Note> {
  const response = await apiClient.delete<SingleNoteApiResponse>(`${BASE_PATH}/${noteId}/tags`, { data: { tags } });
  return response.data.data;
}

/**
 * Archive or unarchive a note
 */
export async function archiveNote(noteId: string, isArchived = true): Promise<Note> {
  const response = await apiClient.post<SingleNoteApiResponse>(`${BASE_PATH}/${noteId}/archive`, {
    is_archived: isArchived,
  });
  return response.data.data;
}

/**
 * Full-text search notes
 */
export async function searchNotes(
  query: string,
  filters?: Partial<NoteFilters>
): Promise<NoteSearchApiResponse['data']> {
  const params: Record<string, string> = { q: query };

  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params[key] = String(value);
      }
    });
  }

  const response = await apiClient.get<NoteSearchApiResponse>(`${BASE_PATH}/search`, { params });
  return response.data.data;
}

/**
 * Fetch version history for a note
 */
export async function fetchVersionHistory(noteId: string): Promise<NoteVersion[]> {
  const response = await apiClient.get<NoteVersionListApiResponse>(`${BASE_PATH}/${noteId}/versions`);
  return response.data.data.versions;
}

/**
 * Restore a note to a previous version
 */
export async function restoreVersion(noteId: string, versionId: string): Promise<Note> {
  const response = await apiClient.post<SingleNoteApiResponse>(
    `${BASE_PATH}/${noteId}/versions/${versionId}/restore`
  );
  return response.data.data;
}
